import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listCustomerColours } from '../productionApi';
import { productionKeys } from '../queryKeys';
import { BarStyle, ColourMap, CUSTOMER_COLOURS } from './barStyle';

/**
 * Fetches the editable per-customer colour map from production.customer_colours
 * and shapes it into the `ColourMap` that `barStyle()` consumes. Every board
 * surface (Gantt bars, Ready-to-Run cards, parked chips) calls this hook;
 * React Query dedupes the request by key, so it's a single fetch.
 *
 * Until the query resolves it returns the seed `CUSTOMER_COLOURS` so the board
 * paints identically to before, with no colour flash.
 */
export function useCustomerColours(): ColourMap {
  const { data } = useQuery({
    queryKey: productionKeys.customerColours(),
    queryFn: listCustomerColours,
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    if (!data) return CUSTOMER_COLOURS;
    const map: ColourMap = {};
    for (const c of data) {
      const style: BarStyle = { fill: c.fill, text: c.text };
      if (c.border) style.border = c.border;
      map[c.customer_code] = style;
    }
    return map;
  }, [data]);
}
